import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, Repeat2, Share, Flag } from "lucide-react";
import LikeButton from "@/components/site/LikeButton";

/**
 * Threads-style action bar for forum posts.
 * - Minimalist outline icons, ≥44px touch targets on mobile.
 * - Like button plays a soft bounce + heart-fill animation on tap.
 * - Share uses Web Share API when available, else falls back to Threads intent URL + clipboard.
 *
 * Props:
 *   liked, likeCount, replyCount, onLike, onReply, onQuote, onShare, shareUrl, shareText, testidPrefix
 *   hideReply — set true for reply cards to hide the reply button (they nest under a common composer)
 *   size — "md" | "sm"
 */
export default function PostActionBar({
  liked = false,
  likeCount = 0,
  replyCount,
  onLike,
  onReply,
  onQuote,
  onShare,
  onReport,
  shareUrl,
  shareText,
  testidPrefix = "post",
  hideReply = false,
  size = "md",
}) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);
  useEffect(() => () => timerRef.current && clearTimeout(timerRef.current), []);

  const doShare = async () => {
    // Custom handler wins if provided
    if (onShare) return onShare();
    const payload = { title: shareText || document.title, text: shareText || "", url: shareUrl || window.location.href };
    try {
      if (navigator.share) {
        await navigator.share(payload);
        return;
      }
    } catch (_) { /* user cancelled — fine */ }
    // Fallback: copy link + open Threads intent
    try {
      await navigator.clipboard.writeText(payload.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (_) { /* noop */ }
    const intent = `https://threads.net/intent/post?text=${encodeURIComponent(`${payload.text || ""} ${payload.url}`.trim())}`;
    window.open(intent, "_blank", "noopener,noreferrer");
  };

  const iconSize = size === "sm" ? 18 : 20;
  const btnCls =
    "inline-flex items-center gap-1.5 min-w-[44px] min-h-[44px] px-2 rounded-full text-[#4B5563] hover:bg-[#F3F1ED] active:bg-[#EAE7E0] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1E3A8A]/30";

  return (
    <div className={"flex items-center gap-1 " + (size === "sm" ? "text-xs" : "text-sm")}
      data-testid={`${testidPrefix}-action-bar`}>
      <LikeButton
        liked={liked}
        count={likeCount}
        onToggle={onLike}
        testid={`${testidPrefix}-like`}
        size={iconSize}
      />

      {!hideReply && (
        <button
          type="button"
          onClick={onReply}
          aria-label="Balas"
          data-testid={`${testidPrefix}-reply`}
          className={btnCls}
        >
          <MessageCircle size={iconSize} strokeWidth={1.75} />
          {typeof replyCount === "number" && replyCount > 0 && <span className="tabular-nums">{replyCount}</span>}
        </button>
      )}

      <button
        type="button"
        onClick={onQuote}
        aria-label="Kutip"
        data-testid={`${testidPrefix}-quote`}
        className={btnCls}
      >
        <Repeat2 size={iconSize} strokeWidth={1.75} />
      </button>

      <button
        type="button"
        onClick={doShare}
        aria-label="Bagikan"
        data-testid={`${testidPrefix}-share`}
        className={btnCls + " relative"}
      >
        <Share size={iconSize} strokeWidth={1.75} />
        {copied && (
          <span className="absolute -top-8 left-1/2 -translate-x-1/2 text-[10px] whitespace-nowrap bg-[#1F2937] text-white px-2 py-0.5 rounded-full">
            Link disalin
          </span>
        )}
      </button>

      {onReport && (
        <button
          type="button"
          onClick={onReport}
          aria-label="Laporkan"
          data-testid={`${testidPrefix}-report`}
          className={btnCls + " hover:text-[#B91C1C]"}
          title="Laporkan konten yang tidak kondusif"
        >
          <Flag size={iconSize} strokeWidth={1.75} />
        </button>
      )}
    </div>
  );
}
